/**
 * Admin Bank Transfer Redux Slice
 * ================================
 * Redux state management for super admin bank transfer operations.
 *
 * Author: SuberPay Team
 * Date: 2026-09-14
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BankAccount, BankTransferRequest } from '../../../types/bank-transfer.types'

export interface AdminBankTransferState {
  // Bank Accounts Management
  bankAccounts: BankAccount[]
  bankAccountsLoading: boolean
  currentBankAccount: BankAccount | null
  savingBankAccount: boolean

  // Transfer Requests Management
  allRequests: any[]
  requestsLoading: boolean
  totalRequests: number

  // Current Request Details (for review)
  currentRequest: BankTransferRequest | null
  currentRequestMessages: any[]
  currentRequestLoading: boolean

  // Actions
  approvingRequest: boolean
  rejectingRequest: boolean
  sendingMessage: boolean
  requestingInfo: boolean

  // Filters
  statusFilter: string | null
  typeFilter: string | null
  currentPage: number
  pageSize: number

  // Errors
  error: string | null
}

const initialState: AdminBankTransferState = {
  bankAccounts: [],
  bankAccountsLoading: false,
  currentBankAccount: null,
  savingBankAccount: false,

  allRequests: [],
  requestsLoading: false,
  totalRequests: 0,

  currentRequest: null,
  currentRequestMessages: [],
  currentRequestLoading: false,

  approvingRequest: false,
  rejectingRequest: false,
  sendingMessage: false,
  requestingInfo: false,

  statusFilter: null,
  typeFilter: 'BANK_TRANSFER',
  currentPage: 1,
  pageSize: 20,

  error: null,
}

const adminBankTransferSlice = createSlice({
  name: 'adminBankTransfer',
  initialState,
  reducers: {
    // Bank Accounts
    fetchBankAccountsStart(state) {
      state.bankAccountsLoading = true
      state.error = null
    },
    fetchBankAccountsSuccess(state, action: PayloadAction<BankAccount[]>) {
      state.bankAccounts = action.payload
      state.bankAccountsLoading = false
    },
    fetchBankAccountsFailure(state, action: PayloadAction<string>) {
      state.bankAccountsLoading = false
      state.error = action.payload
    },

    // Save Bank Account (Create/Update)
    saveBankAccountStart(state) {
      state.savingBankAccount = true
      state.error = null
    },
    saveBankAccountSuccess(state, action: PayloadAction<BankAccount>) {
      state.savingBankAccount = false
      const index = state.bankAccounts.findIndex(acc => acc.id === action.payload.id)
      if (index !== -1) {
        state.bankAccounts[index] = action.payload
      } else {
        state.bankAccounts.unshift(action.payload)
      }
      state.currentBankAccount = null
    },
    saveBankAccountFailure(state, action: PayloadAction<string>) {
      state.savingBankAccount = false
      state.error = action.payload
    },

    // Delete Bank Account
    deleteBankAccountSuccess(state, action: PayloadAction<string>) {
      state.bankAccounts = state.bankAccounts.filter(acc => acc.id !== action.payload)
    },

    // Set Current Bank Account
    setCurrentBankAccount(state, action: PayloadAction<BankAccount | null>) {
      state.currentBankAccount = action.payload
    },

    // All Requests
    fetchAllRequestsStart(state) {
      state.requestsLoading = true
      state.error = null
    },
    fetchAllRequestsSuccess(state, action: PayloadAction<{ requests: any[]; total_count: number }>) {
      state.allRequests = action.payload.requests
      state.totalRequests = action.payload.total_count
      state.requestsLoading = false
    },
    fetchAllRequestsFailure(state, action: PayloadAction<string>) {
      state.requestsLoading = false
      state.error = action.payload
    },

    // Current Request Details
    fetchRequestDetailsStart(state) {
      state.currentRequestLoading = true
      state.error = null
    },
    fetchRequestDetailsSuccess(state, action: PayloadAction<{ transfer: BankTransferRequest; messages: any[] }>) {
      state.currentRequest = action.payload.transfer
      state.currentRequestMessages = action.payload.messages
      state.currentRequestLoading = false
    },
    fetchRequestDetailsFailure(state, action: PayloadAction<string>) {
      state.currentRequestLoading = false
      state.error = action.payload
    },

    // Approve Request
    approveRequestStart(state) {
      state.approvingRequest = true
      state.error = null
    },
    approveRequestSuccess(state, action: PayloadAction<BankTransferRequest>) {
      state.approvingRequest = false
      state.currentRequest = action.payload
      // Update in list
      const index = state.allRequests.findIndex(req => req.reference === action.payload.reference)
      if (index !== -1) {
        state.allRequests[index] = { ...state.allRequests[index], ...action.payload }
      }
    },
    approveRequestFailure(state, action: PayloadAction<string>) {
      state.approvingRequest = false
      state.error = action.payload
    },

    // Reject Request
    rejectRequestStart(state) {
      state.rejectingRequest = true
      state.error = null
    },
    rejectRequestSuccess(state, action: PayloadAction<BankTransferRequest>) {
      state.rejectingRequest = false
      state.currentRequest = action.payload
      // Update in list
      const index = state.allRequests.findIndex(req => req.reference === action.payload.reference)
      if (index !== -1) {
        state.allRequests[index] = { ...state.allRequests[index], ...action.payload }
      }
    },
    rejectRequestFailure(state, action: PayloadAction<string>) {
      state.rejectingRequest = false
      state.error = action.payload
    },

    // Request Additional Info
    requestInfoStart(state) {
      state.requestingInfo = true
      state.error = null
    },
    requestInfoSuccess(state, action: PayloadAction<any>) {
      state.requestingInfo = false
      state.currentRequestMessages.push(action.payload)
    },
    requestInfoFailure(state, action: PayloadAction<string>) {
      state.requestingInfo = false
      state.error = action.payload
    },

    // Send Message
    sendMessageStart(state) {
      state.sendingMessage = true
      state.error = null
    },
    sendMessageSuccess(state, action: PayloadAction<any>) {
      state.sendingMessage = false
      state.currentRequestMessages.push(action.payload)
    },
    sendMessageFailure(state, action: PayloadAction<string>) {
      state.sendingMessage = false
      state.error = action.payload
    },

    // Filters
    setStatusFilter(state, action: PayloadAction<string | null>) {
      state.statusFilter = action.payload
      state.currentPage = 1
    },
    setTypeFilter(state, action: PayloadAction<string | null>) {
      state.typeFilter = action.payload
      state.currentPage = 1
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },

    // Clear
    clearCurrentRequest(state) {
      state.currentRequest = null
      state.currentRequestMessages = []
    },
    clearError(state) {
      state.error = null
    },
  },
})

export const {
  fetchBankAccountsStart,
  fetchBankAccountsSuccess,
  fetchBankAccountsFailure,

  saveBankAccountStart,
  saveBankAccountSuccess,
  saveBankAccountFailure,

  deleteBankAccountSuccess,
  setCurrentBankAccount,

  fetchAllRequestsStart,
  fetchAllRequestsSuccess,
  fetchAllRequestsFailure,

  fetchRequestDetailsStart,
  fetchRequestDetailsSuccess,
  fetchRequestDetailsFailure,

  approveRequestStart,
  approveRequestSuccess,
  approveRequestFailure,

  rejectRequestStart,
  rejectRequestSuccess,
  rejectRequestFailure,

  requestInfoStart,
  requestInfoSuccess,
  requestInfoFailure,

  sendMessageStart,
  sendMessageSuccess,
  sendMessageFailure,

  setStatusFilter,
  setTypeFilter,
  setCurrentPage,

  clearCurrentRequest,
  clearError,
} = adminBankTransferSlice.actions

export default adminBankTransferSlice.reducer
